import React, { useState, useCallback, useEffect } from "react";
import { motion } from "framer-motion";
import { Bed, Bath, Car, Square, ArrowRight, Heart } from "lucide-react";
import { Link } from "react-router-dom";
import { Home } from "../types";
import {
  useAnimationConfig,
  useReducedMotion,
} from "../lib/animations/context";
import { getHomeDisplayName } from "../lib/homes";
import { useFavoriteAnimation } from "../lib/animations/useFavoriteAnimation";
import { animated } from "@react-spring/web";

interface Enhanced3DHomeCardProps {
  home: Home;
  showCategory?: boolean;
  featured?: boolean;
  animationDelay?: number;
  onFavoriteToggle?: (homeId: string) => void;
  isFavorite?: boolean;
}

const Enhanced3DHomeCard: React.FC<Enhanced3DHomeCardProps> = ({
  home,
  showCategory = true,
  featured = false,
  animationDelay = 0,
  onFavoriteToggle,
  isFavorite = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const config = useAnimationConfig();
  const isReducedMotion = useReducedMotion();

  // Use favorite animation hook
  const favoriteAnimation = useFavoriteAnimation({
    bounceIntensity: 1.4,
    celebrationDuration: 0.7,
    pulseCount: 2,
  });

  // Simple mouse handlers
  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  const handleFavoriteClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // Trigger appropriate animation based on current state
      if (isFavorite) {
        favoriteAnimation.triggerUnfavorite();
      } else {
        favoriteAnimation.triggerFavorite();
      }

      onFavoriteToggle?.(home.id);
    },
    [home.id, onFavoriteToggle, isFavorite, favoriteAnimation]
  );

  const categoryColors = {
    core: "bg-blue-100 text-blue-800",
    family: "bg-green-100 text-green-800",
    kiwiana: "bg-amber-100 text-amber-800",
  };

  const categoryNames = {
    core: "Core",
    family: "Family",
    kiwiana: "Kiwiana",
  };

  // Animation variants for entrance
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: isReducedMotion ? 0 : 50,
      scale: isReducedMotion ? 1 : 0.9,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: config.customTimings.normal,
        delay: animationDelay,
        ease: config.customTimings.defaultEasing,
      },
    },
  };

  // Overlay animation variants
  const overlayVariants = {
    hidden: {
      opacity: 0,
      y: isReducedMotion ? 0 : 20,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: config.customTimings.fast,
        ease: config.customTimings.defaultEasing,
      },
    },
  };

  return (
    <Link to={`/homes/${home.category}/${home.slug}`} className="block">
      <motion.div
        className="group relative bg-white rounded-2xl shadow-lg hover:shadow-xl overflow-hidden cursor-pointer transition-all duration-300 transform hover:-translate-y-1"
        variants={cardVariants}
        initial="hidden"
        animate="visible"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Featured Badge */}
        {featured && (
          <motion.div
            className="absolute top-4 left-4 z-20 bg-[#FF5A1F] text-white px-3 py-1 rounded-full text-sm font-semibold"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{
              delay: animationDelay + 0.3,
              duration: config.customTimings.fast,
            }}
          >
            Featured
          </motion.div>
        )}

        {/* Category Badge */}
        {showCategory && (
          <motion.div
            className={`absolute top-4 right-4 z-20 px-3 py-1 rounded-full text-sm font-medium ${
              categoryColors[home.category]
            }`}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{
              delay: animationDelay + 0.2,
              duration: config.customTimings.fast,
            }}
          >
            {categoryNames[home.category]}
          </motion.div>
        )}

        {/* Favorite Button */}
        <motion.button
          onClick={handleFavoriteClick}
          className={`absolute top-4 ${
            showCategory ? "right-20" : "right-4"
          } z-20 p-2 rounded-full backdrop-blur-sm transition-all duration-200 ${
            isFavorite
              ? "bg-red-500 text-white shadow-lg"
              : "bg-white/80 text-gray-600 hover:bg-white hover:text-red-500"
          }`}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{
            delay: animationDelay + 0.4,
            duration: config.customTimings.fast,
          }}
          disabled={favoriteAnimation.isAnimating}
        >
          <div className="relative">
            {/* Main heart icon */}
            <animated.div style={favoriteAnimation.springProps}>
              <Heart
                className={`w-4 h-4 ${isFavorite ? "fill-current" : ""}`}
              />
            </animated.div>

            {/* Celebration effect */}
            <animated.div
              className="absolute inset-0 pointer-events-none"
              style={favoriteAnimation.celebrationProps}
            >
              <Heart className="w-4 h-4 fill-current text-red-400 opacity-60" />
            </animated.div>
          </div>
        </motion.button>

        {/* Image Container with 3D transform */}
        <div className="relative h-64 overflow-hidden">
          <motion.img
            src={home.heroImage}
            alt={getHomeDisplayName(home)}
            className="w-full h-full object-cover"
            animate={{
              scale: isHovered && !isReducedMotion ? 1.1 : 1,
            }}
            transition={{
              duration: config.customTimings.normal,
              ease: config.customTimings.defaultEasing,
            }}
            style={{
              transformOrigin: "center center",
            }}
          />

          {/* Hover Overlay */}
          <motion.div
            className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"
            variants={overlayVariants}
            initial="hidden"
            animate={isHovered ? "visible" : "hidden"}
          />

          {/* Hover Details Overlay */}
          <motion.div
            className="absolute inset-0 flex items-end p-6"
            variants={overlayVariants}
            initial="hidden"
            animate={isHovered ? "visible" : "hidden"}
          >
            <div className="text-white">
              <h4 className="text-lg font-bold mb-2">
                {getHomeDisplayName(home)}
              </h4>
              <p className="text-sm opacity-90 mb-3 line-clamp-2">
                {home.keyFeatures.slice(0, 2).join(" • ")}
              </p>
              <motion.div
                className="flex items-center space-x-2 text-sm"
                initial={{ y: 10, opacity: 0 }}
                animate={
                  isHovered ? { y: 0, opacity: 1 } : { y: 10, opacity: 0 }
                }
                transition={{ delay: 0.1 }}
              >
                <span className="bg-white/20 backdrop-blur-sm px-2 py-1 rounded">
                  {home.specs.totalArea}m²
                </span>
                <span className="bg-white/20 backdrop-blur-sm px-2 py-1 rounded">
                  {home.specs.bedrooms} bed
                </span>
                <span className="bg-white/20 backdrop-blur-sm px-2 py-1 rounded">
                  {home.specs.bathrooms} bath
                </span>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="flex items-start justify-between mb-3">
            <motion.h3
              className="text-xl font-bold text-[#1F2937] group-hover:text-[#FF5A1F] transition-colors duration-200"
              style={{ transformStyle: "preserve-3d" }}
            >
              {getHomeDisplayName(home)}
            </motion.h3>
          </div>

          {/* Specs */}
          <motion.div
            className="flex items-center space-x-4 mb-4 text-[#334155]"
            style={{ transformStyle: "preserve-3d" }}
          >
            <motion.div
              className="flex items-center space-x-1"
              whileHover={{ scale: 1.05 }}
            >
              <Bed className="w-4 h-4" />
              <span className="text-sm font-medium">{home.specs.bedrooms}</span>
            </motion.div>
            <motion.div
              className="flex items-center space-x-1"
              whileHover={{ scale: 1.05 }}
            >
              <Bath className="w-4 h-4" />
              <span className="text-sm font-medium">
                {home.specs.bathrooms}
              </span>
            </motion.div>
            {home.specs.garage && (
              <motion.div
                className="flex items-center space-x-1"
                whileHover={{ scale: 1.05 }}
              >
                <Car className="w-4 h-4" />
                <span className="text-sm font-medium">{home.specs.garage}</span>
              </motion.div>
            )}
            <motion.div
              className="flex items-center space-x-1"
              whileHover={{ scale: 1.05 }}
            >
              <Square className="w-4 h-4" />
              <span className="text-sm font-medium">
                {home.specs.totalArea}m²
              </span>
            </motion.div>
          </motion.div>

          {/* Key Features */}
          <div className="mb-4">
            <p className="text-sm text-[#334155] line-clamp-2">
              {home.keyFeatures.slice(0, 2).join(" • ")}
            </p>
          </div>

          {/* CTA */}
          <div className="inline-flex items-center space-x-2 text-[#FF5A1F] hover:text-[#e4501c] font-semibold transition-colors duration-200 group/link">
            <span>View Details</span>
            <motion.div
              whileHover={{ x: 4 }}
              transition={{ duration: config.customTimings.fast }}
            >
              <ArrowRight className="w-4 h-4" />
            </motion.div>
          </div>
        </div>
      </motion.div>
    </Link>
  );
};

export default Enhanced3DHomeCard;
